import {
	useState,
	useEffect,
	useCallback
} from "react";
import { Guid } from "client_legacy/ndaw/guid";
import {
	AfasLiveInformationProvider,
	InterestedAfasParty,
	InterestedAfasPartyForResourceKind
} from "client/Features/Workspace/Tiles/Afas/provider";
import { AfasListItem, AfasResourceKind, AfasResultStatus } from "client/Api/Workspace/LiveInformation/Afas/afasLiveInformation";

type LoadingState<TResourceKind extends AfasResourceKind> =
	{
		isLoading: true;
		response?: LazyLoadPageResult<AfasListItem<TResourceKind>>;
	}
	|
	(
		{
			isLoading: false;
		} & (
			{
				hasTooManyConsecutiveErrors: true;
				hasGlobalFailure?: undefined;
				response?: undefined;
				error?: undefined;
			}
			|
			{
				hasGlobalFailure: true;
				hasTooManyConsecutiveErrors?: undefined;
				response?: undefined;
				error?: undefined;
			}
			|
			{
				error: AfasResultStatus.NoAccess | AfasResultStatus.RequireNewToken;
				hasGlobalFailure?: undefined;
				hasTooManyConsecutiveErrors?: undefined;
				response?: undefined;
			}
			|
			{
				response: LazyLoadPageResult<AfasListItem<TResourceKind>>;
				error?: undefined;
				hasGlobalFailure?: undefined;
				hasTooManyConsecutiveErrors?: undefined;
			}
		)
	);

type AfasLiveInformationData<TResourceKind extends AfasResourceKind> = {
	setLoadingState: (value: React.SetStateAction<LoadingState<TResourceKind>>) => void;
	loadingState: LoadingState<TResourceKind>;
	reload: () => void;
};

export function useAfasLiveInformation<TResourceKind extends AfasResourceKind>(
	provider: AfasLiveInformationProvider,
	tabId: Guid,
	resourceKind: TResourceKind,
	connectionId: number,
	suspendLiveInformation: boolean = false
): AfasLiveInformationData<TResourceKind> {
	if (resourceKind !== AfasResourceKind.MySignals && resourceKind !== AfasResourceKind.MyTasks) {
		throw new Error("unexpected resourceKind");
	}

	const [loadingState, setLoadingState] = useState<LoadingState<TResourceKind>>({
		isLoading: true
	});

	useEffect(
		(): EffectWithDestructorResult => {
			if (suspendLiveInformation) {
				return;
			}

			const party: InterestedAfasPartyForResourceKind<TResourceKind> = {
				partyId: tabId,
				resourceKind: resourceKind,
				resourceId: {
					connectionId: connectionId,
					resourceKind: resourceKind
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true,
					});
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				informAboutAccessViolation: () => {
					setLoadingState({
						isLoading: false,
						error: AfasResultStatus.NoAccess
					});
				},
				informAboutNewTokenRequirement: () => {
					setLoadingState({
						isLoading: false,
						error: AfasResultStatus.RequireNewToken
					});
				},
				updateItems: (items: LazyLoadPageResult<AfasListItem<TResourceKind>>): void => {
					setLoadingState({
						isLoading: false,
						response: items
					});
				},
			};

			setLoadingState({
				isLoading: true
			});

			// despite checking the type of the resourceKind in top of the function typescript
			// is not smart enough to realize the type of the party
			const interestedParty = party as InterestedAfasParty;

			provider.register(
				interestedParty,
				{ items: true }
			);

			return () => {
				provider.unregister(interestedParty);
			};
		},
		[
			resourceKind,
			provider,
			tabId,
			connectionId,
			suspendLiveInformation
		]
	);

	const reload = useCallback(
		(): void => {
			provider.forceReload();
		},
		[provider]
	);

	return {
		loadingState,
		setLoadingState,
		reload
	};
}
